import { Box, Button, Divider, Flex, Input, Text, Textarea } from '@mantine/core';
import { useState } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { BiChevronDown, BiPlus } from 'react-icons/bi';
import { BsTrash, BsGripVertical } from 'react-icons/bs';
import { Popover } from 'react-tiny-popover';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import calendarIcon from '../../../../assets/icons/calendar.png';
import numberIcon from '../../../../assets/icons/numbers.png';
import typographyIcon from '../../../../assets/icons/typography.png';
import { DraggablePortal } from '../../../../components/DraggablePortal';

const INITIAL_FIELD_STATE = { name: '', type: 'string' };
const INITIAL_TABLE_STATE = { name: '', global_custom_fields: [{ ...INITIAL_FIELD_STATE }] };

export default function AddDataCategory({ onClose, onSubmit, isSubmitting }) {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      is_fund: 'non-asset',
      name: '',
      description: '',
      custom_category_tables: [INITIAL_TABLE_STATE]
    }
  });

  const {
    fields: tables,
    append: appendTable,
    remove: removeTable
  } = useFieldArray({
    control,
    name: 'custom_category_tables'
  });

  const addTableItem = () => appendTable({ ...INITIAL_TABLE_STATE });

  return (
    <Box
      id="data-extraction-add-category-root"
      w="100%"
      display="flex"
      h="100%"
      sx={{ flexDirection: 'column', gap: 24 }}
    >
      <Text id="data-extraction-add-category-description" c="gray.7" fz={14}>
        Create a custom Data Category tailored to the specific structure and logic of your input
        data.
      </Text>

      <form id="data-extraction-add-category-form" onSubmit={handleSubmit(onSubmit)}>
        <Flex
          id="data-extraction-add-category-form-body"
          w="100%"
          sx={{ flexDirection: 'column', gap: 16 }}
        >
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box id="data-extraction-add-category-name-field">
                <Text id="data-extraction-add-category-name-label" c="dark.6" fz="14px" fw={600}>
                  Category Name
                </Text>
                <Input
                  id="data-extraction-add-category-name-input"
                  required
                  placeholder="Enter Data Category name"
                  {...field}
                  error={error}
                />
              </Box>
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box id="data-extraction-add-category-description-field">
                <Text
                  id="data-extraction-add-category-description-label"
                  c="dark.6"
                  fz="14px"
                  fw={600}
                >
                  Category Description
                </Text>
                <Textarea
                  id="data-extraction-add-category-description-input"
                  placeholder="Enter Data Category description"
                  {...field}
                  error={error}
                />
              </Box>
            )}
          />

          {tables.map((table, idx) => (
            <CustomTableItem
              key={table.id}
              control={control}
              tableIndex={idx}
              tableLength={tables.length}
              removeTableItem={removeTable}
            />
          ))}
        </Flex>

        <Button
          id="data-extraction-add-category-add-table-button"
          mt={16}
          leftSection={<BiPlus />}
          variant="outline"
          size="xs"
          onClick={addTableItem}
          type="button"
        >
          Add New Table
        </Button>

        <Flex
          id="data-extraction-add-category-footer"
          w="100%"
          justify="space-between"
          mt={32}
          pos="sticky"
          bg="white"
          bottom={0}
          py={12}
        >
          <Button
            id="data-extraction-add-category-cancel-button"
            variant="outline"
            c="dark.8"
            data-testid="cancel-button"
            bd="1px solid #CED4DA"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            id="data-extraction-add-category-save-button"
            type="submit"
            size="sm"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Flex>
      </form>
    </Box>
  );
}

function CustomTableItem({ control, tableIndex, tableLength, removeTableItem }) {
  const [openFieldPopover, setOpenFieldPopover] = useState(false);

  const {
    fields: fieldsArray,
    append: appendField,
    remove: removeField,
    move: moveField,
    update: updateField
  } = useFieldArray({
    control,
    name: `custom_category_tables.${tableIndex}.global_custom_fields`
  });

  const handleSelectFieldType = type => {
    appendField({ ...INITIAL_FIELD_STATE, type });
    setOpenFieldPopover(false);
  };

  const handleEditFieldType = (type, fieldIndex, value) => {
    updateField(fieldIndex, { ...fieldsArray[fieldIndex], name: value, type });
  };

  const handleDragEnd = result => {
    if (!result.destination) return;
    moveField(result.source.index, result.destination.index);
  };

  return (
    <>
      <Controller
        name={`custom_category_tables.${tableIndex}.name`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box id={`data-extraction-add-category-table-block-${tableIndex}`}>
            <Text
              id={`data-extraction-add-category-table-label-${tableIndex}`}
              c="dark.6"
              fz="14px"
              fw={600}
            >
              Table Name {tableLength > 1 && tableIndex + 1}
            </Text>
            <Box
              id={`data-extraction-add-category-table-row-${tableIndex}`}
              display="flex"
              sx={{ alignItems: 'center' }}
            >
              <Input
                id={`data-extraction-add-category-table-input-${tableIndex}`}
                required
                placeholder="Enter Table name"
                {...field}
                error={error}
                flex={1}
              />
              {tableLength > 1 && (
                <Button
                  id={`data-extraction-add-category-remove-table-${tableIndex}`}
                  variant="transparent"
                  size="xs"
                  onClick={() => removeTableItem(tableIndex)}
                  data-testid="delete-section"
                >
                  <BsTrash
                    id={`data-extraction-add-category-remove-table-icon-${tableIndex}`}
                    color="gray"
                  />
                </Button>
              )}
            </Box>

            <Box id={`data-extraction-add-category-fields-block-${tableIndex}`} mt={16} ml={16}>
              <Text
                id={`data-extraction-add-category-fields-label-${tableIndex}`}
                c="dark.6"
                fz="14px"
                fw={600}
              >
                Required Fields for Custom Table
              </Text>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={`droppable-${tableIndex}`}>
                  {provided => (
                    <Box
                      id={`data-extraction-add-category-droppable-${tableIndex}`}
                      mt={8}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        position: 'relative'
                      }}
                    >
                      {fieldsArray.map((field, fIdx) => (
                        <Draggable
                          key={field.id}
                          draggableId={`${tableIndex}-${field.id}`}
                          index={fIdx}
                        >
                          {(providedDrag, snapshot) => {
                            const content = (
                              <Box
                                id={`data-extraction-add-category-field-wrapper-${tableIndex}-${fIdx}`}
                                ref={providedDrag.innerRef}
                                {...providedDrag.draggableProps}
                                {...providedDrag.dragHandleProps}
                              >
                                <CustomFieldItem
                                  control={control}
                                  field={field}
                                  fieldIndex={fIdx}
                                  tableIdx={tableIndex}
                                  removeFieldItem={removeField}
                                  editFieldType={handleEditFieldType}
                                />
                              </Box>
                            );

                            return snapshot.isDragging ? (
                              <DraggablePortal>{content}</DraggablePortal>
                            ) : (
                              content
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>

              <Popover
                isOpen={openFieldPopover}
                onClickOutside={() => setOpenFieldPopover(false)}
                positions={['bottom']}
                content={
                  <Box
                    id={`data-extraction-add-category-field-popover-${tableIndex}`}
                    p={4}
                    display="flex"
                    sx={{
                      flexDirection: 'column',
                      borderRadius: 4,
                      border: '1px solid #CED4DA',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    <FieldTypeButton
                      icon={numberIcon}
                      label="Numeric"
                      onClick={() => handleSelectFieldType('number')}
                    />
                    <FieldTypeButton
                      icon={typographyIcon}
                      label="Text"
                      onClick={() => handleSelectFieldType('string')}
                    />
                    <FieldTypeButton
                      icon={calendarIcon}
                      label="Date"
                      onClick={() => handleSelectFieldType('datetime')}
                    />
                  </Box>
                }
              >
                <Button
                  id={`data-extraction-add-category-add-field-button-${tableIndex}`}
                  mt={8}
                  leftSection={<BiPlus />}
                  rightSection={<BiChevronDown />}
                  variant="outline"
                  size="xs"
                  bd={0}
                  onClick={() => setOpenFieldPopover(true)}
                >
                  Add Required Field
                </Button>
              </Popover>
            </Box>
          </Box>
        )}
      />

      {tableIndex !== tableLength - 1 && tableLength > 1 && (
        <Divider id={`data-extraction-add-category-divider-${tableIndex}`} my={2} />
      )}
    </>
  );
}

function CustomFieldItem({ control, field, fieldIndex, tableIdx, removeFieldItem, editFieldType }) {
  const [openFieldPopover, setOpenFieldPopover] = useState(false);

  const handleSelectFieldType = (type, value) => {
    editFieldType(type, fieldIndex, value);
    setOpenFieldPopover(false);
  };

  const _img =
    field.type === 'number'
      ? numberIcon
      : field.type === 'datetime'
        ? calendarIcon
        : typographyIcon;

  return (
    <Controller
      name={`custom_category_tables.${tableIdx}.global_custom_fields.${fieldIndex}.name`}
      control={control}
      rules={{
        required: 'Field name is required',
        validate: (value, formValues) => {
          const fields = formValues.custom_category_tables[tableIdx].global_custom_fields;
          const duplicateCount = fields.filter(f => f.name === value).length;
          return duplicateCount <= 1 || 'Field name must be unique';
        }
      }}
      render={({ field, fieldState: { error } }) => (
        <>
          <Box
            id={`data-extraction-add-category-field-row-${tableIdx}-${fieldIndex}`}
            display="flex"
            sx={{ alignItems: 'center', gap: 8 }}
          >
            <BsGripVertical size={18} color="gray" style={{ cursor: 'grab' }} />
            <Input
              id={`data-extraction-add-category-field-input-${tableIdx}-${fieldIndex}`}
              required
              placeholder="Enter Field name"
              {...field}
              error={error}
              flex={1}
              leftSectionPointerEvents="pointer"
              leftSection={
                <Popover
                  isOpen={openFieldPopover}
                  onClickOutside={() => setOpenFieldPopover(false)}
                  positions={['bottom']}
                  content={
                    <Box
                      p={4}
                      display="flex"
                      ml={32}
                      sx={{
                        flexDirection: 'column',
                        borderRadius: 4,
                        border: '1px solid #CED4DA',
                        background: '#fff',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.05)'
                      }}
                    >
                      <FieldTypeButton
                        icon={numberIcon}
                        label="Numeric"
                        onClick={() => handleSelectFieldType('number', field.value)}
                      />
                      <FieldTypeButton
                        icon={typographyIcon}
                        label="Text"
                        onClick={() => handleSelectFieldType('string', field.value)}
                      />
                      <FieldTypeButton
                        icon={calendarIcon}
                        label="Date"
                        onClick={() => handleSelectFieldType('datetime', field.value)}
                      />
                    </Box>
                  }
                >
                  <Button
                    id={`data-extraction-add-category-field-type-trigger-${tableIdx}-${fieldIndex}`}
                    variant="transparent"
                    onClick={() => setOpenFieldPopover(true)}
                    p={0}
                  >
                    <img src={_img} width={20} />
                  </Button>
                </Popover>
              }
            />
            <Button
              id={`data-extraction-add-category-remove-field-${tableIdx}-${fieldIndex}`}
              variant="transparent"
              size="xs"
              onClick={() => removeFieldItem(fieldIndex)}
            >
              <BsTrash color="gray" />
            </Button>
          </Box>
          {error && (
            <Text
              id={`data-extraction-add-category-field-error-${tableIdx}-${fieldIndex}`}
              c="red"
              fz={12}
              mt={4}
              ml={30}
            >
              {error.message}
            </Text>
          )}
        </>
      )}
    />
  );
}

function FieldTypeButton({ icon, label, onClick }) {
  return (
    <Button
      id={`data-extraction-add-category-field-type-button-${label}`}
      size="xs"
      variant="transparent"
      c="dark"
      leftSection={<img width={16} height={16} src={icon} />}
      onClick={onClick}
    >
      <Text
        id={`data-extraction-add-category-field-type-label-${label}`}
        w={50}
        fz={12}
        fw={400}
        ta="left"
      >
        {label}
      </Text>
    </Button>
  );
}
